<template>
    <div class="field">
        <label class="field__label" :for="inputId">{{ label }}</label>
        <div class="password-field">
            <input
                :id="inputId"
                :value="modelValue"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="new-password"
                :class="['form-input', { 'is-invalid': Boolean(error) }]"
                :disabled="disabled"
                :readonly="locked"
                @input="emit('update:modelValue', $event.target.value)"
            />
            <button type="button" class="password-field__action" :disabled="disabled || locked" @click="generatePassword">
                Сгенерировать
            </button>
            <button
                v-if="!locked"
                type="button"
                class="password-field__action"
                :disabled="!modelValue || disabled"
                @click="isPasswordVisible = !isPasswordVisible"
            >
                {{ isPasswordVisible ? 'Скрыть' : 'Показать' }}
            </button>
            <button type="button" class="password-field__action" :disabled="!modelValue || disabled" @click="copyPassword">
                Копировать
            </button>
        </div>
        <p v-if="error" class="field__error">{{ error }}</p>
        <p v-else-if="$slots.hint" class="field__hint"><slot name="hint" /></p>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useToast } from '../../../composables/useToast';

const GENERATED_PASSWORD_LENGTH = 16;
const PASSWORD_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%*-_';

const props = defineProps({
    modelValue: { type: String, default: '' },
    inputId: { type: String, required: true },
    label: { type: String, default: 'Пароль' },
    error: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    locked: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const toast = useToast();
const isPasswordVisible = ref(false);
const showPassword = computed(() => props.locked || isPasswordVisible.value);

watch(() => props.modelValue, (value) => {
    if (!value) isPasswordVisible.value = false;
});

const generatePassword = () => {
    const values = crypto.getRandomValues(new Uint32Array(GENERATED_PASSWORD_LENGTH));
    emit('update:modelValue', Array.from(values, value => PASSWORD_ALPHABET[value % PASSWORD_ALPHABET.length]).join(''));
    isPasswordVisible.value = true;
};

const copyPassword = async () => {
    try {
        await navigator.clipboard.writeText(props.modelValue);
        toast.info('Пароль скопирован');
    } catch {
        toast.error('Не удалось скопировать — выделите пароль и скопируйте вручную');
    }
};
</script>

<style scoped lang="scss">
@use '../../../../scss/pages/admin/admin-tokens' as *;
@use '../../../../scss/pages/admin/admin-form';

@include admin-form.fields;

.password-field {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-sm;

    .form-input {
        flex: 1 1 200px;
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    }
}

.password-field__action {
    padding: 0 $spacing-md;
    border: 1px solid $admin-border-hover;
    border-radius: $radius-md;
    background: $admin-bg-primary;
    font-size: $font-size-sm;
    color: $admin-text-secondary;
    cursor: pointer;

    &:hover:not(:disabled) { background: $admin-bg-hover; }

    &:disabled {
        cursor: default;
        opacity: 0.6;
    }
}
</style>
